import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { nth } from "lodash/fp";
import { Slider, Tracks, Rail, Handles } from "react-compound-slider";
import "@reach/auto-id";
import { useCachedValue } from "../../hooks/use-cached-value.es.js";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { MultiSliderHandle } from "./multi-slider-handle.es.js";
import { MultiSliderWrapper, MultiSliderImpl, MultiSliderTrack, MultiSliderRail } from "./multi-slider.styles.es.js";
const propTypes = {
  disabled: PropTypes.bool,
  enableTooltips: PropTypes.bool,
  highlightTrack: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onSlideEnd: PropTypes.func,
  onSlideStart: PropTypes.func,
  step: PropTypes.number,
  valueLabels: PropTypes.arrayOf(PropTypes.string),
  values: PropTypes.arrayOf(PropTypes.number).isRequired
};
const defaultProps = {
  disabled: false,
  enableTooltips: false,
  highlightTrack: false,
  onSlideEnd: void 0,
  onSlideStart: void 0,
  step: 1,
  valueLabels: []
};
const MultiSlider = ({
  disabled,
  enableTooltips,
  highlightTrack,
  max,
  min,
  onChange,
  onSlideEnd,
  onSlideStart,
  step,
  valueLabels,
  values
}) => {
  const domain = useMemo(() => [min, max], [max, min]);
  const [isSliding, setSliding] = useState(false);
  const cachedValues = useCachedValue(values, isSliding);
  const handlePointerDown = useCallback((event) => {
    const { currentTarget, pointerId } = event;
    if (currentTarget.setPointerCapture) {
      currentTarget.setPointerCapture(pointerId);
    }
  }, []);
  const handlePointerUp = useCallback((event) => {
    const { currentTarget, pointerId } = event;
    if (currentTarget.releasePointerCapture) {
      currentTarget.releasePointerCapture(pointerId);
    }
  }, []);
  const handleSlideStart = useCallback(() => {
    setSliding(true);
    if (onSlideStart) {
      onSlideStart();
    }
  }, [onSlideStart]);
  const handleSlideEnd = useCallback(() => {
    setSliding(false);
    if (onSlideEnd) {
      onSlideEnd();
    }
  }, [onSlideEnd]);
  return /* @__PURE__ */ jsx(MultiSliderWrapper, { children: /* @__PURE__ */ jsxs(
    MultiSliderImpl,
    {
      as: Slider,
      disabled,
      domain,
      onSlideEnd: handleSlideEnd,
      onSlideStart: handleSlideStart,
      onUpdate: onChange,
      rootProps: {
        onPointerDown: handlePointerDown,
        onPointerUp: handlePointerUp
      },
      step,
      values: cachedValues,
      children: [
        highlightTrack && /* @__PURE__ */ jsx(Tracks, { left: false, right: false, children: ({ tracks, getTrackProps }) => /* @__PURE__ */ jsx(Fragment, { children: tracks.map((track) => /* @__PURE__ */ jsx(
          MultiSliderTrack,
          {
            disabled,
            left: `${track.source.percent}%`,
            right: `${100 - track.target.percent}%`,
            ...getTrackProps(track.id)
          },
          track.id
        )) }) }),
        /* @__PURE__ */ jsx(Rail, { children: ({ getRailProps }) => /* @__PURE__ */ jsx(MultiSliderRail, { ...getRailProps() }) }),
        /* @__PURE__ */ jsx(Handles, { children: ({ handles, activeHandleID, getHandleProps } = {}) => /* @__PURE__ */ jsx(Fragment, { children: handles.map((handle, i) => /* @__PURE__ */ jsx(
          MultiSliderHandle,
          {
            active: handle.id === activeHandleID,
            "data-handle-id": handle.id,
            disabled,
            enableTooltip: enableTooltips,
            max,
            min,
            percent: handle.percent,
            value: handle.value,
            valueLabel: nth(i, valueLabels),
            ...getHandleProps(handle.id)
          },
          handle.id
        )) }) })
      ]
    }
  ) });
};
MultiSlider.propTypes = propTypes;
MultiSlider.defaultProps = defaultProps;
export {
  MultiSlider
};
